import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Filter } from 'src/app/shared/models/filter';
import { environment } from 'src/environments/environment';
import { HydraCollectionDto, InvoiceCollectionResponseDto, InvoiceDto, RevenueInvoiceEventDto, RevenueInvoiceEventUpdateDto } from '../dtos/invoice.dto';
import { SendShippingEmailDto, ShippingAccountingCollectionDto } from '../dtos/shipping-accounting.dto';
import { InvoiceExportRequest } from '../../domain/models/invoice-export.model';

@Injectable()
export class InvoiceApiService {
  private readonly invoicesUrl = `${environment.url}/view/service/invoices`;
  private readonly legacyRevenueInvoicesUrl = `${environment.url}/revenues/invoices`;
  private readonly revenueInvoiceEventsUrl = `${environment.url}/revenue_invoice_events`;
  private readonly invoiceCardCsvProperties: readonly string[] = [
    'revenue',
    'revenueDate',
    'status',
    'issuerType',
    'unit.legalName',
    'unit.tradeName',
    'unit.registrationNumber',
    'professional.name',
    'professional.company.legalName',
    'professional.company.registrationNumber',
    'professional.company.tradeName',
    'payer.name',
    'payer.taxId',
    'service.name',
    'service.specialty.name',
    'effectiveAmount',
    'calculatedAmount',
    'revenueAmount'
  ];

  constructor(private readonly httpClient: HttpClient) {}

  getInvoices(query: string): Observable<HydraCollectionDto<InvoiceDto>> {
    return this.httpClient.get<InvoiceCollectionResponseDto>(`${this.invoicesUrl}${query}`).pipe(
      map((response) => this.normalizeCollection(response))
    );
  }

  getIssuedCount(): Observable<number> {
    return this.getCountByStatus('issued');
  }

  getErrorCount(): Observable<number> {
    return this.getCountByStatus('register_error', 'failed');
  }

  cancelInvoice(id: number | string): Observable<void> {
    return this.httpClient.delete<void>(`${this.legacyRevenueInvoicesUrl}/${id}`);
  }

  resendInvoice(id: number | string): Observable<void> {
    return this.httpClient.get(`${environment.nami_job_url}/job/invoices/revenues/${id}`, { responseType: 'text' }).pipe(map(() => undefined));
  }

  downloadDanfe(serviceInvoiceId: number | string): Observable<Blob> {
    return this.httpClient.get(`${environment.nami_url}/invoice/${serviceInvoiceId}/pdf`, { responseType: 'blob' });
  }

  sendEmail(serviceInvoiceId: number | string): Observable<void> {
    return this.httpClient.post<void>(`${environment.nami_url}/invoice/sendmail`, { invoiceId: serviceInvoiceId });
  }

  getShippingAccounting(query: string): Observable<ShippingAccountingCollectionDto> {
    return this.httpClient.get<ShippingAccountingCollectionDto>(`${environment.url}/invoices/mails/sents${query}`);
  }

  sendShippingEmail(dto: SendShippingEmailDto): Observable<void> {
    return this.httpClient.post<void>(`${environment.url}/invoices/mails/resend`, dto);
  }

  downloadShippingCsv(professionalId: string, startDate: string, endDate: string): Observable<Blob> {
    const query = new Filter()
      .filter('professional', professionalId)
      .filter('startDate', startDate)
      .filter('endDate', endDate)
      .convertFilterInQuery();
    return this.httpClient.get(`${environment.url}/invoices/downloads/csv${query}`, { responseType: 'blob' });
  }

  exportInvoicesCsv(request: InvoiceExportRequest): Observable<Blob> {
    const params = this.createExportParams(request);
    const headers = new HttpHeaders({ Accept: 'text/csv' });

    return this.httpClient.get(this.invoicesUrl, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  getRevenueInvoiceEvent(revenueId: number): Observable<RevenueInvoiceEventDto> {
    return this.httpClient.get<RevenueInvoiceEventDto>(`${this.revenueInvoiceEventsUrl}/${revenueId}`);
  }

  updateRevenueInvoiceEvent(dto: RevenueInvoiceEventUpdateDto): Observable<RevenueInvoiceEventDto> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json',
    });

    return this.httpClient.patch<RevenueInvoiceEventDto>(`${this.revenueInvoiceEventsUrl}/${dto.id}`, dto, { headers });
  }

  private createExportParams(request: InvoiceExportRequest): HttpParams {
    let params = new HttpParams();

    this.invoiceCardCsvProperties.forEach((property) => {
      const [relation, ...relationPropertyParts] = property.split('.');
      const relationProperty = relationPropertyParts.join('.');
      const key = relationProperty ? `properties[${relation}][]` : 'properties[]';
      params = params.append(key, relationProperty || property);
    });

    request.filters.forEach((filter) => {
      if (filter.value === undefined || filter.value === null || !filter.property) return;

      let key = filter.property;
      if (filter.fields) {
        key += filter.fields.length > 0 ? `[${filter.fields.join(',')}]` : '[]';
      }

      params = params.append(key, String(filter.value));
    });

    return params;
  }

  private getCountByStatus(...statuses: readonly string[]): Observable<number> {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const after = this.toLocalDateString(firstDay);
    const before = this.toLocalDateString(lastDay);
    const filter = new Filter()
      .date('revenueDate', before, 'before')
      .date('revenueDate', after, 'after');

    statuses.forEach((status) => {
      filter.filter('status', status, 'IN');
    });

    return this.httpClient.get<InvoiceCollectionResponseDto>(`${this.invoicesUrl}${filter.convertFilterInQuery()}`)
      .pipe(map((response) => this.normalizeCollection(response)))
      .pipe(map((response) => response['hydra:totalItems']));
  }

  private toLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private normalizeCollection(response: InvoiceCollectionResponseDto): HydraCollectionDto<InvoiceDto> {
    if (Array.isArray(response)) {
      return {
        'hydra:member': response,
        'hydra:totalItems': response.length
      };
    }

    if ('hydra:member' in response) {
      return response;
    }

    const members = response.data ?? response.items ?? [];

    return {
      'hydra:member': members,
      'hydra:totalItems': response.totalItems ?? response.total ?? members.length
    };
  }
}
